import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { api, apiErrorMessage } from "../lib/api";
import { toast } from "sonner";
import { TID } from "../lib/testIds";
import { Link } from "react-router-dom";
import { Link2, KeyRound, ExternalLink, AlertTriangle } from "lucide-react";

const METHODS = [
  { value: "playwright", label: "Playwright (recomendado)" },
  { value: "browser", label: "Navegador manual" },
];

const EMPTY = {
  name: "",
  url: "",
  user_or_email: "",
  access_method: "playwright",
  notes: "",
  profile: "",
  logged_in_selector: "",
};

export default function AddWebServiceDialog({
  open,
  onOpenChange,
  onCreated,
  onUpdated,
  editing = null,
}) {
  const isEdit = Boolean(editing);
  const [form, setForm] = useState(
    isEdit
      ? {
          name: editing.name || "",
          url: editing.url || "",
          user_or_email: editing.user_or_email || "",
          access_method: editing.access_method || "playwright",
          notes: editing.notes || "",
          profile: editing.profile || "",
          logged_in_selector: editing.logged_in_selector || "",
        }
      : EMPTY
  );
  const [saving, setSaving] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [profilesError, setProfilesError] = useState(null);

  // Load available browser profiles when the dialog opens. We only fetch on
  // open (not on mount) to avoid needless upstream calls.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setProfilesLoading(true);
      setProfilesError(null);
      try {
        const { data } = await api.get("/browser/profiles");
        if (!cancelled) setProfiles(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) {
          setProfiles([]);
          setProfilesError(apiErrorMessage(err));
        }
      } finally {
        if (!cancelled) setProfilesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const currentProfile = form.profile || "";
  const currentProfileObj = profiles.find((p) => p.name === currentProfile) || null;
  const currentProfileMissing =
    currentProfile !== "" &&
    !profilesLoading &&
    profiles.every((p) => p.name !== currentProfile);
  const currentProfileNoStorage =
    !!currentProfileObj && currentProfileObj.has_storage_state === false;
  const missingSelector =
    form.access_method === "playwright" &&
    currentProfile !== "" &&
    !form.logged_in_selector.trim();

  function close() {
    onOpenChange(false);
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.url.trim()) {
      toast.error("Nombre y URL son obligatorios");
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        url: form.url.trim(),
        user_or_email: form.user_or_email.trim(),
        access_method: form.access_method,
        notes: form.notes,
        profile: form.profile.trim() || null,
        logged_in_selector: form.logged_in_selector.trim() || null,
      };
      if (isEdit) {
        const { data } = await api.patch(`/web-services/${editing.id}`, body);
        onUpdated?.(data);
        toast.success("Servicio actualizado");
      } else {
        const { data } = await api.post("/web-services", body);
        onCreated?.(data);
        toast.success("Servicio creado");
      }
      close();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent
        data-testid={TID.webServices.dialog}
        className="bg-zinc-950 border border-zinc-800 text-zinc-100 sm:max-w-[560px]"
      >
        <DialogHeader>
          <DialogTitle className="text-white">
            {isEdit ? "Editar servicio" : "Añadir servicio"}
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Registra un servicio web. Sólo se guardan metadatos: nunca contraseñas ni cookies.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Nombre">
            <Input
              data-testid={TID.webServices.fieldName}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Ej: Gemini Web"
              className="bg-zinc-900 border-zinc-800 focus-visible:ring-zinc-400"
            />
          </Field>
          <Field label="URL de acceso">
            <Input
              data-testid={TID.webServices.fieldUrl}
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder="https://gemini.google.com/"
              className="bg-zinc-900 border-zinc-800 focus-visible:ring-zinc-400 font-mono text-sm"
            />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Usuario / correo">
              <Input
                data-testid={TID.webServices.fieldUser}
                value={form.user_or_email}
                onChange={(e) => setForm({ ...form, user_or_email: e.target.value })}
                placeholder="nombre@dominio.com"
                className="bg-zinc-900 border-zinc-800 focus-visible:ring-zinc-400"
              />
            </Field>
            <Field label="Método de acceso">
              <Select
                value={form.access_method}
                onValueChange={(v) => setForm({ ...form, access_method: v })}
              >
                <SelectTrigger
                  data-testid={TID.webServices.fieldMethod}
                  className="bg-zinc-900 border-zinc-800 text-zinc-100"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                  {METHODS.map((m) => (
                    <SelectItem key={m.value} value={m.value} className="focus:bg-zinc-900">
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Notas">
            <Textarea
              data-testid={TID.webServices.fieldNotes}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              placeholder="Perfil, workspace, particularidades del login…"
              className="bg-zinc-900 border-zinc-800 focus-visible:ring-zinc-400"
            />
          </Field>

          {form.access_method === "playwright" && (
            <div className="rounded-md border border-zinc-800 bg-black/40 p-3 space-y-3">
              <div className="flex items-center gap-2 text-[11px] text-zinc-400">
                <Link2 className="h-3.5 w-3.5" />
                Vincular con un perfil de Navegador (opcional)
              </div>
              <p className="text-[11px] text-zinc-500 -mt-1 leading-relaxed">
                Si vinculas un perfil y añades un selector, "Comprobar conexión" hará
                una verificación real end-to-end contra KRAI API. Si lo dejas vacío,
                hará solo un health-check de la cadena.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Perfil del Navegador">
                  <Select
                    value={currentProfile || "__none__"}
                    onValueChange={(v) =>
                      setForm({ ...form, profile: v === "__none__" ? "" : v })
                    }
                  >
                    <SelectTrigger
                      data-testid={TID.webServices.fieldProfile}
                      className="bg-zinc-900 border-zinc-800 font-mono text-sm"
                    >
                      <SelectValue
                        placeholder={profilesLoading ? "Cargando…" : "Sin vincular"}
                      />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                      <SelectItem value="__none__" className="font-mono">
                        <span className="text-zinc-500">— sin vincular —</span>
                      </SelectItem>
                      {profiles.map((p) => (
                        <SelectItem
                          key={p.name}
                          value={p.name}
                          className="font-mono"
                        >
                          {p.name}
                          {p.has_storage_state === false && (
                            <span className="ml-2 text-[10px] text-amber-400">
                              (sin storage_state)
                            </span>
                          )}
                        </SelectItem>
                      ))}
                      {currentProfileMissing && (
                        <SelectItem value={currentProfile} className="font-mono text-amber-300">
                          {currentProfile}
                          <span className="ml-2 text-[10px]">(no existe)</span>
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  <div className="flex items-center justify-between gap-2 pt-1.5">
                    {profilesError ? (
                      <span className="text-[10px] text-red-400">
                        No se pudieron cargar perfiles: {profilesError}
                      </span>
                    ) : profiles.length === 0 && !profilesLoading ? (
                      <span className="text-[10px] text-zinc-500">
                        No hay perfiles todavía.
                      </span>
                    ) : (
                      <span className="text-[10px] text-zinc-500">
                        {profiles.length} perfil{profiles.length === 1 ? "" : "es"} disponible{profiles.length === 1 ? "" : "s"}.
                      </span>
                    )}
                    <Link
                      to="/browser"
                      onClick={close}
                      className="inline-flex items-center gap-1 text-[10px] text-zinc-300 hover:text-white underline underline-offset-2"
                    >
                      Crear perfil <ExternalLink className="h-2.5 w-2.5" />
                    </Link>
                  </div>
                  {currentProfileMissing && (
                    <div className="flex items-start gap-1.5 pt-1.5 text-[10px] text-amber-300">
                      <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                      El perfil "{currentProfile}" no existe en Navegador. Créalo
                      antes de "Comprobar conexión".
                    </div>
                  )}
                  {currentProfileNoStorage && !currentProfileMissing && (
                    <div
                      data-testid={TID.webServices.warnNoStorageState}
                      className="flex items-start gap-1.5 pt-1.5 text-[10px] text-amber-300"
                    >
                      <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                      Este perfil todavía no tiene una sesión guardada
                      (storage_state). El servicio no podrá verificar la
                      conexión hasta que se cargue una sesión desde{" "}
                      <Link
                        to="/browser"
                        onClick={close}
                        className="underline underline-offset-2 hover:text-white"
                      >
                        Navegador / Playwright
                      </Link>
                      .
                    </div>
                  )}
                </Field>
                <Field label="Selector CSS de sesión activa">
                  <Input
                    data-testid={TID.webServices.fieldLoggedInSel}
                    value={form.logged_in_selector}
                    onChange={(e) => setForm({ ...form, logged_in_selector: e.target.value })}
                    placeholder='[data-user-avatar]  |  .user-menu'
                    className="bg-zinc-900 border-zinc-800 font-mono text-sm"
                  />
                  {missingSelector && (
                    <div
                      data-testid={TID.webServices.warnMissingSelector}
                      className="flex items-start gap-1.5 pt-1.5 text-[10px] text-amber-300"
                    >
                      <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                      No has configurado un selector de sesión. La conexión no
                      podrá verificar si el usuario está realmente autenticado
                      (solo comprobará la salud de la cadena).
                    </div>
                  )}
                </Field>
              </div>
            </div>
          )}

          <div className="rounded-md border border-emerald-900/40 bg-emerald-950/15 p-3">
            <div className="flex items-center gap-2 text-[11px] text-emerald-300 mb-1.5">
              <KeyRound className="h-3.5 w-3.5" />
              Credenciales — dónde viven
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed">
              Servicios web es solo el catálogo. Las contraseñas se guardan cifradas
              (AES-256-GCM) en el módulo{" "}
              <Link
                to="/sessions"
                className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2"
                onClick={close}
              >
                Sesiones
              </Link>{" "}
              o mediante <span className="font-mono text-zinc-300">storage_state</span>{" "}
              en{" "}
              <Link
                to="/browser"
                className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2"
                onClick={close}
              >
                Navegador / Playwright
              </Link>
              . Para Google/Gemini y similares usa siempre <b>Navegador</b> (login manual una vez).
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={close}
              className="bg-zinc-900 hover:bg-zinc-800 border border-zinc-800"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              data-testid={TID.webServices.submit}
              disabled={saving}
              className="bg-white text-black hover:bg-zinc-200"
            >
              {saving ? "Guardando…" : isEdit ? "Guardar cambios" : "Crear servicio"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-zinc-300">{label}</Label>
      {children}
    </div>
  );
}
